/**
 * Instagram 新規投稿 Modal — Media Picker(debug spec §3-§7 修正版)。
 *
 * 仕様:
 *   - ユーザーは管理タイトル未入力でもファイルを選択できる
 *   - 選択ファイルはローカル File を保持し object URL でプレビュー
 *   - Modal 关闭またはコンポーネント unmount 時に object URL を解放
 *   - 真の Upload は管理タイトルが入力され submit されたタイミングで実行
 *     (呼び元: `uploadPendingFiles(title)` を親側で呼ぶ)
 *   - Upload 前 rename: `{title}_{yyyyMMdd_HHmmss}[_NN].{ext}`
 *   - Upload は既存 mediaApi.uploadMedia(`/api/media/upload`)
 *
 * debug spec §3.5: 不要 `disabled={!title}` を Upload ゾーンに付ける。
 */

import { useEffect, useRef, useState } from "react";
import {
  FileImage,
  ImageIcon,
  Loader2,
  Upload,
  Video as VideoIcon,
  X,
} from "lucide-react";

import { listMedia, uploadMedia } from "../../media/api/mediaApi";

// ---------------------------------------------------------------------------
// 公開型(spec §7.2 + debug §3.3 PendingMedia)
// ---------------------------------------------------------------------------

export type SelectedInstagramMedia = {
  mediaId: string;
  publicUrl: string;
  thumbnailUrl: string | null;
  fileName: string;
  mimeType: string;
  size?: number | null;
};

/**
 * debug §3.3: アップロード待ちの生 File。
 * originalFile を必ず保持(object URL だけだと Submit 時に本体が消える)。
 */
export type PendingMedia = {
  id: string;
  originalFile: File;
  previewUrl: string;
};

// ---------------------------------------------------------------------------
// File rename helpers(debug §4.1-§4.3)
// ---------------------------------------------------------------------------

function sanitizeFileBaseName(value: string): string {
  const sanitized = value
    .trim()
    .replace(/[\\/:*?"<>|#%{}[\]]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized || "instagram_post";
}

function formatFileTimestamp(date = new Date()): string {
  const pad = (v: number) => String(v).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    "_",
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
  ].join("");
}

/**
 * debug §4.3: 真 Upload 直前に File をリネーム。
 * binary / mime / lastModified は変えない。
 */
export function renameMediaFile(
  file: File,
  managementTitle: string,
  index: number,
  total: number,
  timestamp: string,
): File {
  const baseName = sanitizeFileBaseName(managementTitle);
  const dotIndex = file.name.lastIndexOf(".");
  const extension =
    dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : "";
  const sequence = total > 1 ? `_${String(index + 1).padStart(2, "0")}` : "";
  const nextName = `${baseName}_${timestamp}${sequence}${extension}`;
  return new File([file], nextName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

// ---------------------------------------------------------------------------
// MediaItem → SelectedInstagramMedia 変換(ライブラリから選択時)
// ---------------------------------------------------------------------------

type MediaListItem = Awaited<ReturnType<typeof listMedia>>["items"][number];

function mapMediaItem(item: MediaListItem): SelectedInstagramMedia {
  return {
    mediaId: String(item.id),
    publicUrl: item.source_url ?? "",
    thumbnailUrl: item.thumbnail_url,
    fileName: item.title ?? item.alt_text ?? `media-${item.id}`,
    mimeType: item.mime_type ?? "application/octet-stream",
    size: null,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstagramMediaPicker({
  managementTitle,
  selected,
  pendingMedia,
  onPendingChange,
  onChange,
  onUploadingChange,
}: {
  /** Modal 親から渡される。Submit 時の検証と rename に使う。 */
  managementTitle: string;
  /** ライブラリ or Upload 完了で確定した SelectedMedia。 */
  selected: SelectedInstagramMedia[];
  /** debug §3.3: アップロード待ち File 一覧。 */
  pendingMedia: PendingMedia[];
  onPendingChange: (next: PendingMedia[]) => void;
  onChange: (next: SelectedInstagramMedia[]) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const [tab, setTab] = useState<"library" | "upload">("library");
  const [items, setItems] = useState<MediaListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // debug §3.5: Modal 关闭 / unmount 時に object URL 解放
  const pendingRef = useRef(pendingMedia);
  pendingRef.current = pendingMedia;
  useEffect(() => {
    return () => {
      pendingRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  // Library tab が開かれたら一覧取得
  useEffect(() => {
    if (tab !== "library") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMedia({ per_page: 24 })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  useEffect(() => {
    onUploadingChange(uploading);
  }, [uploading, onUploadingChange]);

  const handleSelectFromLibrary = (item: MediaListItem) => {
    const mapped = mapMediaItem(item);
    if (!mapped.publicUrl) return;
    onChange([mapped]);
    // ライブラリを選んだら pending はクリア(混在を避ける)
    pendingRef.current.forEach((it) => URL.revokeObjectURL(it.previewUrl));
    onPendingChange([]);
    setError(null);
  };

  /**
   * debug §3.4: ファイル選択時に **Upload しない**。
   * File を pendingMedia に保存して object URL でプレビューするだけ。
   * タイトル空でも OK。
   */
  const handleFilesSelected = (files: FileList | File[] | null) => {
    if (!files) return;
    const nextFiles = Array.from(files);
    if (nextFiles.length === 0) return;
    const nextMedia = nextFiles.map((file) => ({
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      originalFile: file,
      previewUrl: URL.createObjectURL(file),
    }));
    onPendingChange([...pendingRef.current, ...nextMedia]);
    setError(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelected(e.target.files);
    e.target.value = ""; // 同名ファイル再選択可
  };

  const removePending = (id: string) => {
    const target = pendingRef.current.find((it) => it.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onPendingChange(pendingRef.current.filter((it) => it.id !== id));
  };

  /**
   * debug §6: 親 Modal が Submit 直前に呼ぶ。
   * 戻り値: upload 後の publicUrl 一覧 + 既存 selectedMedia の publicUrl。
   * `managementTitle` 空ならエラーを throw(親側で先に検証する責務)。
   */
  const uploadPendingFiles = async (): Promise<SelectedInstagramMedia[]> => {
    if (pendingMedia.length === 0) return selected;
    const title = managementTitle.trim();
    if (!title) {
      throw new Error("管理用タイトルを入力してください。");
    }
    const timestamp = formatFileTimestamp();
    const total = pendingMedia.length;
    const renamedFiles = pendingMedia.map((item, index) =>
      renameMediaFile(item.originalFile, title, index, total, timestamp),
    );

    setUploading(true);
    try {
      const uploaded: SelectedInstagramMedia[] = [];
      for (const file of renamedFiles) {
        const item = await uploadMedia({ file, title: file.name });
        if (!item.source_url) {
          throw new Error("アップロード後の公開URLが取得できませんでした");
        }
        uploaded.push({
          mediaId: String(item.id),
          publicUrl: item.source_url,
          thumbnailUrl: item.thumbnail_url ?? null,
          fileName: item.title ?? file.name,
          mimeType: item.mime_type ?? file.type,
          size: null,
        });
      }
      // previewUrl 解放
      pendingRef.current.forEach((it) => URL.revokeObjectURL(it.previewUrl));
      onPendingChange([]);
      // debug §6: 既存 selected と結合(現状 API は最初の 1 枚のみなので先頭優先)
      const merged = [...selected, ...uploaded];
      onChange(merged.length > 0 ? [merged[0]] : merged);
      return merged.length > 0 ? [merged[0]] : [];
    } finally {
      setUploading(false);
    }
  };

  // 親が呼ぶ hook として export
  (InstagramMediaPicker as unknown as {
    uploadPendingFiles: typeof uploadPendingFiles;
  }).uploadPendingFiles = uploadPendingFiles;

  const isVideoMime = (m: string) => m.startsWith("video/");

  return (
    <div>
      {/* Tab(debug §7) */}
      <div className="ig-media-tabs">
        <button
          type="button"
          className={`ig-media-tab ${tab === "library" ? "is-active" : ""}`}
          onClick={() => setTab("library")}
        >
          メディアライブラリ
        </button>
        <button
          type="button"
          className={`ig-media-tab ${tab === "upload" ? "is-active" : ""}`}
          onClick={() => setTab("upload")}
        >
          アップロード
        </button>
      </div>

      {tab === "library" ? (
        <LibraryTab
          loading={loading}
          error={error}
          items={items}
          selected={selected}
          onSelect={handleSelectFromLibrary}
          isVideoMime={isVideoMime}
        />
      ) : (
        <UploadTab
          pendingMedia={pendingMedia}
          uploading={uploading}
          onFileChange={handleFileInputChange}
          onRemovePending={removePending}
          isVideoMime={isVideoMime}
        />
      )}

      {error ? <div className="ig-ai-error">{error}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library Tab
// ---------------------------------------------------------------------------

function LibraryTab({
  loading,
  error,
  items,
  selected,
  onSelect,
  isVideoMime,
}: {
  loading: boolean;
  error: string | null;
  items: MediaListItem[];
  selected: SelectedInstagramMedia[];
  onSelect: (item: MediaListItem) => void;
  isVideoMime: (m: string) => boolean;
}) {
  if (loading) {
    return (
      <div
        style={{
          padding: 16,
          textAlign: "center",
          color: "var(--ig-muted)",
          fontSize: 12,
        }}
      >
        <Loader2
          size={16}
          className="ig-spin"
          style={{ verticalAlign: "middle", marginRight: 6 }}
        />
        メディアライブラリを読み込み中...
      </div>
    );
  }
  if (error) {
    return <div className="ig-ai-error">{error}</div>;
  }
  if (items.length === 0) {
    return (
      <div className="ig-media-tile-empty">
        メディアライブラリに画像がありません
      </div>
    );
  }
  return (
    <div className="ig-media-grid">
      {items.map((item) => {
        const mapped = mapMediaItem(item);
        const isSel = selected.some((s) => s.mediaId === mapped.mediaId);
        const type = isVideoMime(item.mime_type ?? "") ? "VIDEO" : "IMAGE";
        return (
          <button
            key={item.id}
            type="button"
            className={`ig-media-tile ${isSel ? "is-selected" : ""}`}
            onClick={() => onSelect(item)}
            disabled={!mapped.publicUrl}
            aria-label={mapped.fileName}
          >
            {mapped.publicUrl ? (
              isVideoMime(item.mime_type ?? "") ? (
                <video src={mapped.publicUrl} muted playsInline />
              ) : (
                <img src={mapped.thumbnailUrl ?? mapped.publicUrl} alt="" />
              )
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ig-muted)",
                }}
              >
                <FileImage size={20} />
              </div>
            )}
            <span className="ig-media-tile-type">{type}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload Tab(debug §7 左カラム順: ドロップゾーン → 選択中プレビュー)
// ---------------------------------------------------------------------------

function UploadTab({
  pendingMedia,
  uploading,
  onFileChange,
  onRemovePending,
  isVideoMime,
}: {
  pendingMedia: PendingMedia[];
  uploading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemovePending: (id: string) => void;
  isVideoMime: (m: string) => boolean;
}) {
  return (
    <>
      {/* debug §7.1: ドロップゾーン。disabled は Upload 中だけ。 */}
      <label className="ig-media-upload-zone">
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={onFileChange}
          style={{ display: "none" }}
          disabled={uploading}
        />
        {uploading ? (
          <>
            <Loader2
              size={20}
              className="ig-spin"
              style={{ marginBottom: 6 }}
            />
            <div>アップロード中...</div>
          </>
        ) : (
          <>
            <Upload size={20} style={{ marginBottom: 6 }} />
            <div>クリックしてファイルを選択</div>
            <div
              style={{
                fontSize: 11,
                color: "var(--ig-muted)",
                marginTop: 4,
              }}
            >
              複数選択可。保存時に自動でファイル名を変更します
            </div>
          </>
        )}
      </label>

      {/* debug §7.1 順序: 「已選択メディア」 */}
      {pendingMedia.length > 0 ? (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ig-muted)",
              marginBottom: 8,
            }}
          >
            選択したメディア({pendingMedia.length})
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {pendingMedia.map((p) => {
              const isVid = isVideoMime(p.originalFile.type);
              return (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 8,
                    border: "1px solid var(--ig-border)",
                    borderRadius: 10,
                    background: "var(--ig-surface-soft)",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#f1f4f8",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      color: "var(--ig-muted)",
                    }}
                  >
                    {isVid ? (
                      <video
                        src={p.previewUrl}
                        muted
                        playsInline
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <img
                        src={p.previewUrl}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ig-text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.originalFile.name}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ig-muted)" }}>
                      {isVid ? "動画" : "画像"} ·{" "}
                      {Math.round(p.originalFile.size / 1024)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ig-composer-close"
                    onClick={() => onRemovePending(p.id)}
                    aria-label="削除"
                    style={{ width: 28, height: 28 }}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// 旧 API:`uploadPendingFiles` を外部から呼ぶ hook。
// debug spec §6 では親 Modal が `resolveMediaUrls()` を実装するべきだが、
// 既存 Modal 側の state を最小修正するため、picker 自体に helper を持たせる。
// (コンポーネントインスタンスメソッドとしてではなく、純粋関数 uploadPendingFiles を export)
// ---------------------------------------------------------------------------

export async function uploadPendingMediaFiles(args: {
  pendingMedia: PendingMedia[];
  managementTitle: string;
  existing: SelectedInstagramMedia[];
  setPendingMedia: (next: PendingMedia[]) => void;
  setSelected: (next: SelectedInstagramMedia[]) => void;
  setUploading: (uploading: boolean) => void;
}): Promise<SelectedInstagramMedia[]> {
  const { pendingMedia, managementTitle, existing } = args;
  if (pendingMedia.length === 0) return existing;

  const title = managementTitle.trim();
  if (!title) {
    throw new Error("管理用タイトルを入力してください。");
  }

  const timestamp = formatFileTimestamp();
  const total = pendingMedia.length;
  const renamedFiles = pendingMedia.map((item, index) =>
    renameMediaFile(item.originalFile, title, index, total, timestamp),
  );

  args.setUploading(true);
  try {
    const uploaded: SelectedInstagramMedia[] = [];
    for (const file of renamedFiles) {
      const item = await uploadMedia({ file, title: file.name });
      if (!item.source_url) {
        throw new Error("アップロード後の公開URLが取得できませんでした");
      }
      uploaded.push({
        mediaId: String(item.id),
        publicUrl: item.source_url,
        thumbnailUrl: item.thumbnail_url ?? null,
        fileName: item.title ?? file.name,
        mimeType: item.mime_type ?? file.type,
        size: null,
      });
    }
    pendingMedia.forEach((it) => URL.revokeObjectURL(it.previewUrl));
    args.setPendingMedia([]);
    // debug §6: 既存 selected と結合(現状 API は最初の 1 枚のみ)
    const merged = [...existing, ...uploaded];
    const first = merged.length > 0 ? [merged[0]] : [];
    args.setSelected(first);
    return first;
  } finally {
    args.setUploading(false);
  }
}